import React, { useRef, useEffect, useState } from 'react';
import styles from '../assets/signatureCanvas.module.css';

const SignatureCanvas = ({ onChange, value }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const signatureLoadedRef = useRef(false); // Track if signature was already loaded to prevent infinite loops
  const hasDrawnRef = useRef(false); // Track if user has actually drawn something on the canvas

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setContext(ctx);
    }
  }, []);

  // Load existing signature if value is provided
  useEffect(() => {
    console.log('🔍 [SIGNATURE] SignatureCanvas useEffect triggered with value:', typeof value, value ? value.substring(0, 50) + '...' : 'null');
    console.log('🔍 [SIGNATURE] Canvas ref:', canvasRef.current);
    console.log('🔍 [SIGNATURE] Context:', context);
    console.log('🔍 [SIGNATURE] Already loaded:', signatureLoadedRef.current);

    // Skip if signature already loaded (prevents infinite loop when onChange updates value)
    if (signatureLoadedRef.current) {
      return;
    }

    if (value && canvasRef.current && context) {
      try {
        // Check if value is a valid base64 string
        if (typeof value === 'string' && value.startsWith('data:image/')) {
          console.log('🔍 [SIGNATURE] Loading signature image from base64...');
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            console.log('✅ [SIGNATURE] Signature image loaded successfully from base64');
            context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            context.drawImage(img, 0, 0);
            // Mark as loaded BEFORE calling onChange to prevent infinite loop
            signatureLoadedRef.current = true;
            // Update form data with canvas data to ensure validation passes
            const dataUrl = canvasRef.current.toDataURL();
            onChange(dataUrl);
          };
          img.onerror = (error) => {
            console.error('❌ [SIGNATURE] Error loading signature image from base64:', error);
          };
          img.src = value;
        }
        // Check if value is a presigned URL (starts with http:// or https://)
        else if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
          console.log('🔍 [SIGNATURE] Loading signature image from presigned URL...');
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            console.log('✅ [SIGNATURE] Signature image loaded successfully from presigned URL');
            context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            context.drawImage(img, 0, 0);
            // Mark as loaded BEFORE calling onChange to prevent infinite loop
            signatureLoadedRef.current = true;
            // Update form data with canvas data to ensure validation passes
            const dataUrl = canvasRef.current.toDataURL();
            onChange(dataUrl);
          };
          img.onerror = (error) => {
            console.error('❌ [SIGNATURE] Error loading signature image from presigned URL:', error);
            console.error('❌ [SIGNATURE] Presigned URL that failed:', value.substring(0, 100) + '...');
          };
          img.src = value;
        } else {
          console.log('🔍 [SIGNATURE] Invalid signature format:', typeof value, value?.substring(0, 50));
        }
      } catch (error) {
        console.error('❌ [SIGNATURE] Error processing signature:', error);
      }
    } else {
      console.log('🔍 [SIGNATURE] Conditions not met for loading signature:', {
        hasValue: !!value,
        hasCanvas: !!canvasRef.current,
        hasContext: !!context
      });
    }
  }, [value, context, onChange]);

  // Get canvas coordinates accounting for CSS scaling
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getCanvasCoords(e);
    context.lineTo(x, y);
    context.stroke();
    // Mark that user has actually drawn something
    hasDrawnRef.current = true;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Only update form data if user has actually drawn something or a signature was loaded
    // This prevents blank canvas from being treated as valid signature
    if (canvasRef.current && (hasDrawnRef.current || signatureLoadedRef.current)) {
      const dataUrl = canvasRef.current.toDataURL();
      onChange(dataUrl);
    }
  };

  const clearCanvas = () => {
    if (context && canvasRef.current) {
      context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      signatureLoadedRef.current = false; // Reset so a new signature can be loaded if needed
      hasDrawnRef.current = false; // Reset drawing flag
      onChange('');
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    startDrawing(e);
  };

  const handleMouseMove = (e) => {
    e.preventDefault();
    draw(e);
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  const handleMouseLeave = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    startDrawing(mouseEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    draw(mouseEvent);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    const mouseEvent = new MouseEvent('mouseup', {});
    stopDrawing();
  };

  return (
    <div className={styles.signatureContainer}>
      <canvas
        ref={canvasRef}
        width={320}
        height={200}
        className={styles.signatureCanvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
      <button
        type="button"
        onClick={clearCanvas}
        className={styles.clearButton}
      >
        Clear
      </button>
    </div>
  );
};

export default SignatureCanvas; 